// Multi-month kit plans for the Place Order → Recommended Cart header.
//
// Deliberately dependency-free: this formula has to stay byte-identical to
// kitQuantityForMonths in form-v2s (helpers/preparedKit.js), because the coach
// builds the cart here and the customer is shown the same cart there. Keeping it
// importable without the rest of utility.js is what keeps it easy to check in
// isolation.
//
// The rule is ported from traya-offline-web, which is where these tiers already
// exist for walk-in customers:
//   src/components/Offline-result/Your-plans/YourPlans.tsx → resolveQty()
// Keep the three in step.

export const ALL_KIT_PLAN_MONTHS = [1, 3, 5, 8, 12];
export const DEFAULT_KIT_PLAN_MONTHS = [1, 3];

// The two pre-existing tiers keep their original wording so nothing changes for
// roles that only ever see those two; the new tiers use the numerals the ticket
// names them by.
const KIT_LABEL_OVERRIDES = { 1: "One Month Kit", 3: "Three Month Kit" };

export const kitPlanLabel = (months) =>
  KIT_LABEL_OVERRIDES[months] || `${months} Month Kit`;

// A bottle of scalp or nourish oil outlasts a month, so these do not go one per
// month like everything else. Same overrides offline-web applies.
const SCALP_OIL_QUANTITIES = { 5: 4, 8: 6, 12: 9 };

const isScalpOil = (name) =>
  ["scalp oil", "nourish oil"].some((needle) =>
    String(name || "").toLowerCase().includes(needle)
  );

/**
 * Per-product quantity for an N-month kit.
 *
 * offline-web reads an explicit count per duration (`_5MonthsItemCount` and
 * friends). The repeat-link payload behind Place Order only guarantees
 * `_1month` and `_3month`, so anything above 3 falls back to offline-web's own
 * fallback: one unit per month, capped at the months of course the product has
 * left. That cap is why all five tiers can be offered unconditionally — an
 * oversized tier degrades to what the customer can actually use instead of
 * over-selling.
 */
export const kitQuantityForMonths = (item, months, { preparedKit = false } = {}) => {
  // The backend's own number wins where it exists — _1month and _3month are
  // guaranteed, so the two pre-existing tiers keep behaving exactly as they did
  // before this feature, and any duration a cohort config supplies is taken as
  // authoritative.
  //
  // Except on a coach-prepared kit, where those figures are not trusted. The
  // repeat-link payload derives them from quantityByCoach, which for a customer
  // with no prescription products is the sum of every order they have ever
  // placed — so a customer with 12 past orders is quoted 12 bottles for a one
  // month kit. Measured on production: of 206 products across 40 store
  // customers, _1month was 1 for 199 of them and 3, 8, 9 or 12 for the rest —
  // order counts, not dosages. Nothing legitimately ships more than one unit a
  // month, so a prepared kit ignores the figure and counts months.
  if (!preparedKit) {
    const backendQuantity = item?.quantity?.[`_${months}month`];
    if (backendQuantity != null) return backendQuantity;
  }

  // Kamdudha-like products ship the same count whatever the duration. Also read
  // off the untrusted figures, so it only applies where those are trusted.
  const oneMonth = item?.quantity?._1month ?? 0;
  const threeMonth = item?.quantity?._3month ?? 0;
  if (!preparedKit && threeMonth === oneMonth) return oneMonth;

  if (isScalpOil(item?.name || item?.title) && SCALP_OIL_QUANTITIES[months]) {
    return SCALP_OIL_QUANTITIES[months];
  }

  // A missing or zero course length means unknown, not exhausted — reading it as
  // exhausted derives 0 and applies an empty cart.
  const course = Number(item?.availableMonths);
  return Number.isFinite(course) && course > 0
    ? Math.min(course, months)
    : months;
};
